import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import KpiCard from "@/components/KpiCard";
import BewertungsschubWidget from "@/components/BewertungsschubWidget";
import {
  ScanLine,
  Activity,
  Star,
  MessageSquare,
  Building2,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

export default function PortalDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const [s, b] = await Promise.all([
          api.get("/portal/dashboard/stats"),
          api.get("/portal/businesses"),
        ]);
        setStats(s.data);
        setBusinesses(b.data.businesses || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  return (
    <div className="space-y-8" data-testid="portal-dashboard-page">
      <header>
        <p className="label-eyebrow">Willkommen</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          Hallo, {user?.contact_name || user?.name}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Übersicht deiner Google-Bewertungen und QR-Aktivitäten.
        </p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="QR-Scans gesamt"
          value={loading ? "…" : stats?.total_scans ?? 0}
          icon={ScanLine}
          testId="portal-kpi-total-scans"
        />
        <KpiCard
          label="Scans heute"
          value={loading ? "…" : stats?.scans_today ?? 0}
          icon={Activity}
          tone="warning"
          testId="portal-kpi-scans-today"
        />
        <KpiCard
          label="Google-Rating"
          value={
            loading
              ? "…"
              : typeof stats?.google_rating === "number"
              ? stats.google_rating.toFixed(1)
              : "—"
          }
          icon={Star}
          tone="success"
          hint={stats?.google_rating == null ? "Nach Verbindung mit Google verfügbar" : "Ø-Bewertung"}
          testId="portal-kpi-rating"
        />
        <KpiCard
          label="Bewertungen"
          value={loading ? "…" : stats?.reviews_count ?? "—"}
          icon={MessageSquare}
          tone="muted"
          hint="Anzahl auf Google"
          testId="portal-kpi-reviews-count"
        />
      </section>

      {/* Bewertungsschub — 7d vs 7d trend + share actions */}
      <section>
        <BewertungsschubWidget />
      </section>

      <section className="card-surface p-5" data-testid="portal-dashboard-businesses">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Deine Firmen</h3>
          </div>
          <Link
            to="/customer/business"
            className="text-xs text-primary flex items-center gap-1 hover:underline"
          >
            Alle ansehen <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Lädt…</div>
        ) : businesses.length ? (
          <ul className="divide-y divide-white/[0.05]">
            {businesses.map((b) => (
              <li key={b.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/customer/business/${b.id}`}
                    className="text-sm font-medium hover:text-primary truncate block"
                    data-testid={`portal-business-link-${b.id}`}
                  >
                    {b.business_name}
                  </Link>
                  <div className="text-xs text-muted-foreground truncate">{b.address}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-heading font-semibold text-primary tabular-nums">
                    {b.total_scans}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Scans
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-white/[0.08] rounded-lg">
            Dir wurden noch keine Firmen zugewiesen.
          </div>
        )}
      </section>
    </div>
  );
}
